import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import { useTranslation } from 'react-i18next';
import basicIcon from 'images/brands-unique/reservamos/sl-basic.svg';
import maxIcon from 'images/brands-unique/reservamos/sl-max.svg';

/**
 * Benefit keys rendered per coverage card. Each maps to the i18n key
 * `service-levels.<coverage>-benefits_<n>`. Single source shared by
 * `ServiceLevels` and this component's defaults.
 */
export const COVERAGE_BENEFITS = ['1', '2', '3', '4'];

/**
 * Thin stroke checkmark used in the coverage benefit list.
 *
 * @param {object} props - color
 * @param {string} props.color - CSS color (or var) applied to the check stroke
 * @returns {JSX.Element} The rendered checkmark
 */
const BenefitCheck = ({ color }) => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flex: '0 0 auto', marginTop: '3px' }}
  >
    <polyline
      points="20 6 9 17 4 12"
      style={{
        stroke: color,
        strokeWidth: 2.5,
        fill: 'none',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    />
  </svg>
);

BenefitCheck.propTypes = {
  color: PropTypes.string.isRequired,
};

/**
 * ServiceLevelOption component.
 *
 * @param {object} props - coverage, isChecked, isUpdating, handleChange, price, benefits
 * @param {string} props.coverage - The coverage type ('basic' or 'max')
 * @param {boolean} [props.isChecked=false] - Flag indicating if the option is checked
 * @param {boolean} [props.isUpdating=false] - Flag indicating if the option is being updated
 * @param {Function} [props.handleChange=null] - Function to handle change events
 * @param {number} [props.price] - The price difference for max coverage
 * @param {Array<string>} props.benefits - Array of benefit keys for the coverage type
 * @returns {JSX.Element|null} The rendered component or null if coverage is not provided
 */
const ServiceLevelOption = ({ coverage, isChecked, isUpdating, handleChange, price, benefits }) => {
  const isBasicCoverage = coverage === 'basic';
  const { t } = useTranslation('purchase');

  const serviceLevelsBenefits = useMemo(() => {
    if (!coverage) return [];
    return benefits.map((number) => {
      return t(`service-levels.${coverage}-benefits`, {
        context: number,
      });
    });
  }, [benefits, coverage, t]);

  if (!serviceLevelsBenefits.length) return null;

  const subtitle = isBasicCoverage
    ? t('service-levels.basic', { context: 'pricing' })
    : t('service-levels.max', { context: 'description' });

  const cardStyle = {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '16px',
    border: isBasicCoverage ? '1px solid #e5e7eb' : '2px solid var(--accent)',
    boxShadow: isBasicCoverage ? 'none' : '0 6px 22px rgba(0, 0, 0, 0.08)',
    padding: '20px',
    height: '100%',
  };

  const toggleCoverage = () => {
    if (handleChange) handleChange({ target: { checked: !isChecked } });
  };

  return (
    <div style={{ position: 'relative', width: '100%', paddingTop: '13px' }}>
      {!isBasicCoverage && (
        <div
          style={{
            position: 'absolute',
            top: '2px',
            left: '22px',
            zIndex: 2,
            background: 'var(--accent)',
            borderRadius: '999px',
            padding: '4px 14px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
          }}
        >
          <Text elementType="span" size="XS" weight="bold" color="white">
            {t('service-levels.most_chosen')}
          </Text>
        </div>
      )}

      <div style={cardStyle}>
        <Spacing justifyContent="space-between" alignItems="flex-start">
          <Spacing size="XS" alignItems="center">
            <Icon size="M" type={isBasicCoverage ? basicIcon : maxIcon} />
            <Spacing vertical size="XS" alignItems="flex-start">
              <Text
                color="primary"
                weight="bold"
                mobileSize="S"
                textAlign="left"
                size={isBasicCoverage ? 'S' : 'M'}
              >
                {t(`service-levels.${coverage}`, {
                  context: 'title',
                })}
              </Text>
              <Text
                color={isBasicCoverage ? 'grayMedium' : 'accent'}
                weight="semibold"
                size="S"
                textAlign="left"
                elementType="span"
              >
                {subtitle}
              </Text>
            </Spacing>
          </Spacing>

          {!isBasicCoverage && (
            <LocalizedCurrency
              mobileSize="M"
              size="L"
              weight="bold"
              price={price}
              currencyStyle="currency"
              color="primary"
            />
          )}
        </Spacing>

        <div style={{ marginTop: '16px' }}>
          <Spacing vertical size="S">
            {serviceLevelsBenefits.map((benefit) => (
              <Spacing size="XS" alignItems="flex-start" key={benefit}>
                <BenefitCheck color={isBasicCoverage ? '#9aa0ab' : 'var(--accent)'} />
                <Text color="primary" size="S">
                  {benefit}
                </Text>
              </Spacing>
            ))}
          </Spacing>
        </div>

        {!isBasicCoverage && handleChange && (
          <div style={{ marginTop: '18px' }}>
            <Button
              variant={isChecked ? 'noFill' : 'accent'}
              fullWidth
              isLoading={isUpdating}
              onClick={toggleCoverage}
            >
              {isChecked ? (
                <Text elementType="span" weight="bold" color="accent">
                  {t('service-levels.added_coverage')}
                </Text>
              ) : (
                <Spacing size="XS" alignItems="center" justifyContent="center">
                  <Text elementType="span" weight="bold" color="white">
                    {t('service-levels.add_coverage')}
                  </Text>
                  <LocalizedCurrency
                    price={price}
                    currencyStyle="currency"
                    color="white"
                    weight="bold"
                  />
                </Spacing>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

ServiceLevelOption.propTypes = {
  coverage: PropTypes.string.isRequired,
  isChecked: PropTypes.bool,
  isUpdating: PropTypes.bool,
  handleChange: PropTypes.func,
  price: PropTypes.number,
  benefits: PropTypes.arrayOf(PropTypes.string),
};

ServiceLevelOption.defaultProps = {
  isChecked: false,
  isUpdating: false,
  handleChange: null,
  price: 0,
  benefits: COVERAGE_BENEFITS,
};

export default ServiceLevelOption;
